import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import { Shell } from "../components/Shell";
import { DEPARTAMENTOS, divipolaCodeFor, municipiosDe } from "../data/colombia";
import {
  ApiError,
  funcionarios as funcionariosApi,
  tenants as tenantsApi,
} from "../lib/api";
import type {
  FuncionarioCreateResponse,
  NivelTenant,
  Tenant,
  TenantCreateBody,
} from "../lib/api";

type Step = 1 | 2 | 3;

interface TenantForm {
  nombre: string;
  nit: string;
  departamento: string;
  municipio: string;
  codigo_divipola: string;
  email_contacto: string;
  telefono: string;
  slug: string;
  nivel: NivelTenant;
  parent_id: string;
}

const NIVEL_OPTS: ReadonlyArray<CustomSelectOption<NivelTenant>> = [
  { value: "nacional", label: "Gobierno Nacional" },
  { value: "regional", label: "Regional" },
  { value: "gobernacion", label: "Gobernación" },
  { value: "municipio", label: "Municipio / Alcaldía" },
  { value: "jac", label: "Junta de Acción Comunal" },
];

interface AdminForm {
  email: string;
  nombre: string;
}

const EMPTY_TENANT: TenantForm = {
  nombre: "",
  nit: "",
  departamento: "",
  municipio: "",
  codigo_divipola: "",
  email_contacto: "",
  telefono: "",
  slug: "",
  nivel: "municipio",
  parent_id: "",
};

export function OnboardingPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [form, setForm] = useState<TenantForm>(EMPTY_TENANT);
  const [admin, setAdmin] = useState<AdminForm>({ email: "", nombre: "" });
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [createdAdmin, setCreatedAdmin] = useState<FuncionarioCreateResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tenantsApi
      .list({})
      .then((r) => setTenants(r.items))
      .catch(() => setTenants([]));
  }, []);

  const submitTenant = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: TenantCreateBody = {
        nombre: form.nombre.trim(),
        nit: form.nit.trim(),
        departamento: form.departamento,
        municipio: form.municipio,
        email_contacto: form.email_contacto.trim().toLowerCase(),
        nivel: form.nivel,
      };
      if (form.codigo_divipola.trim()) body.codigo_divipola = form.codigo_divipola.trim();
      if (form.telefono.trim()) body.telefono = form.telefono.trim();
      if (form.slug.trim()) body.slug = form.slug.trim();
      if (form.parent_id) body.parent_id = form.parent_id;
      const t = await tenantsApi.create(body);
      setTenant(t);
      // Sugiere email del admin con dominio del email de contacto
      const domain = body.email_contacto.split("@")[1] ?? "";
      setAdmin({
        email: domain ? `admin@${domain}` : "",
        nombre: "",
      });
      setStep(2);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error de red",
      );
    } finally {
      setBusy(false);
    }
  };

  const submitAdmin = async () => {
    if (!tenant) return;
    setBusy(true);
    setError(null);
    try {
      const r = await funcionariosApi.create(tenant.id, {
        email: admin.email.trim().toLowerCase(),
        nombre: admin.nombre.trim(),
        rol: "admin_tenant",
      });
      setCreatedAdmin(r);
      setStep(3);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? typeof err.detail === "string"
            ? err.detail
            : err.message
          : "Error de red",
      );
    } finally {
      setBusy(false);
    }
  };

  const departamentoOpts: ReadonlyArray<CustomSelectOption<string>> = [
    { value: "", label: "Selecciona departamento…" },
    ...DEPARTAMENTOS.map((d) => ({ value: d, label: d })),
  ];
  const municipioOpts: ReadonlyArray<CustomSelectOption<string>> = form.departamento
    ? [
        { value: "", label: "Selecciona municipio…" },
        ...municipiosDe(form.departamento).map((m) => ({ value: m, label: m })),
      ]
    : [{ value: "", label: "Primero elige departamento" }];

  const canSubmit1 =
    form.nombre.trim().length >= 2 &&
    form.nit.trim().length >= 5 &&
    form.departamento &&
    form.municipio &&
    form.email_contacto.includes("@");
  const canSubmit2 = admin.email.includes("@") && admin.nombre.trim().length >= 2;

  return (
    <Shell>
      <section className="page">
        <div style={{ marginBottom: 24 }}>
          <div className="eyebrow">Plataforma · Onboarding</div>
          <h1>Nueva alcaldía</h1>
          <p className="lede">
            Tres pasos guiados: datos legales, primer administrador y
            credenciales. Las dependencias y plantillas se siembran
            automáticamente.
          </p>
        </div>

        <StepBar step={step} />

        {error && <div className="alert err">{error}</div>}

        {step === 1 && (
          <div className="card">
            <div className="card-inner">
              <div className="eyebrow">Paso 1 · Datos de la alcaldía</div>
              <h2 style={{ marginTop: 4 }}>Identidad</h2>

              <div className="form-row">
                <label className="label">Nombre oficial</label>
                <input
                  className="input"
                  placeholder="Alcaldía de Cajicá"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label className="label">NIT</label>
                <input
                  className="input"
                  placeholder="800.094.701-1"
                  value={form.nit}
                  onChange={(e) => setForm({ ...form, nit: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label className="label">Departamento</label>
                <CustomSelect<string>
                  value={form.departamento}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      departamento: v,
                      municipio: "",
                      codigo_divipola: "",
                    })
                  }
                  options={departamentoOpts}
                />
              </div>
              <div className="form-row">
                <label className="label">Municipio</label>
                <CustomSelect<string>
                  value={form.municipio}
                  onChange={(v) =>
                    setForm({
                      ...form,
                      municipio: v,
                      codigo_divipola: divipolaCodeFor(form.departamento, v) ?? "",
                    })
                  }
                  options={municipioOpts}
                  disabled={!form.departamento}
                />
              </div>
              {form.codigo_divipola && (
                <p style={{ color: "var(--ink-3)", fontSize: 13, marginTop: -10 }}>
                  Código DIVIPOLA detectado: <code>{form.codigo_divipola}</code>
                </p>
              )}
              <div className="form-row">
                <label className="label">Email de contacto</label>
                <input
                  className="input"
                  placeholder="contacto@alcaldia.gov.co"
                  value={form.email_contacto}
                  onChange={(e) =>
                    setForm({ ...form, email_contacto: e.target.value })
                  }
                />
              </div>
              <div className="form-row">
                <label className="label">Teléfono (opcional)</label>
                <input
                  className="input"
                  placeholder="(601) 123 4567"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label className="label">Slug del subdominio (opcional)</label>
                <input
                  className="input"
                  placeholder="cajica  →  cajica.mipuebloapp.com"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  pattern="[a-z0-9-]+"
                />
                <p className="helper">
                  Si lo dejas vacío se genera del nombre.
                </p>
              </div>
              <div className="form-row">
                <label className="label">Nivel jerárquico</label>
                <CustomSelect<NivelTenant>
                  value={form.nivel}
                  onChange={(v) => setForm({ ...form, nivel: v })}
                  options={NIVEL_OPTS}
                  aria-label="Nivel jerárquico"
                />
              </div>

              <div className="form-row">
                <label className="label">Entidad superior (padre)</label>
                <CustomSelect<string>
                  value={form.parent_id}
                  onChange={(v) => setForm({ ...form, parent_id: v })}
                  options={[
                    { value: "", label: "— Sin padre (raíz) —" },
                    ...tenants.map((t) => ({ value: t.id, label: t.nombre })),
                  ]}
                  aria-label="Entidad superior"
                />
                <p className="helper">
                  Una gobernación cuelga del Gobierno Nacional; una alcaldía, de su gobernación.
                </p>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                <Link to="/tenants" className="btn-outline">
                  Cancelar
                </Link>
                <button
                  type="button"
                  className="cta"
                  style={{ width: "auto", flex: 1 }}
                  disabled={busy || !canSubmit1}
                  onClick={() => void submitTenant()}
                >
                  <span className="cta-inner">
                    {busy ? "Creando alcaldía…" : "Siguiente: primer admin →"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 2 && tenant && (
          <div className="card">
            <div className="card-inner">
              <div className="eyebrow">Paso 2 · Primer administrador</div>
              <h2 style={{ marginTop: 4 }}>Cuenta de admin_tenant</h2>
              <p style={{ color: "var(--ink-3)", marginBottom: 16 }}>
                Esta persona podrá gestionar funcionarios, equipos, canales,
                SLA, plantillas, routing y branding de <strong>{tenant.nombre}</strong>.
                Se generará un password temporal que deberá cambiar al primer
                login.
              </p>

              <div className="form-row">
                <label className="label">Email</label>
                <input
                  className="input"
                  type="email"
                  value={admin.email}
                  onChange={(e) =>
                    setAdmin({ ...admin, email: e.target.value })
                  }
                  placeholder={`admin@${tenant.slug}.gov.co`}
                />
              </div>
              <div className="form-row">
                <label className="label">Nombre completo</label>
                <input
                  className="input"
                  value={admin.nombre}
                  onChange={(e) =>
                    setAdmin({ ...admin, nombre: e.target.value })
                  }
                  placeholder="Juana Pérez"
                />
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setStep(1)}
                >
                  ← Atrás
                </button>
                <button
                  type="button"
                  className="cta"
                  style={{ width: "auto", flex: 1 }}
                  disabled={busy || !canSubmit2}
                  onClick={() => void submitAdmin()}
                >
                  <span className="cta-inner">
                    {busy ? "Creando admin…" : "Crear admin y finalizar →"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && tenant && createdAdmin && (
          <div className="card">
            <div className="card-inner">
              <div className="eyebrow">Paso 3 · Listo</div>
              <h2 style={{ marginTop: 4 }}>{tenant.nombre} ya está activa</h2>

              <p style={{ marginBottom: 16 }}>
                Comparte estas credenciales con el admin por canal seguro
                (Signal, WhatsApp 1‑a‑1, llamada). El password{" "}
                <strong>no se vuelve a mostrar</strong>.
              </p>

              <div className="alert warn" style={{ display: "grid", gap: 6 }}>
                <div>
                  <strong>URL de login: </strong>
                  <a
                    href={`https://${tenant.slug}.mipuebloapp.com/login`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {tenant.slug}.mipuebloapp.com/login
                  </a>
                </div>
                <div>
                  <strong>Email: </strong>
                  <code>{createdAdmin.email}</code>
                </div>
                <div>
                  <strong>Password temporal: </strong>
                  <code
                    style={{
                      fontFamily: "var(--font-mono)",
                      background: "rgba(0,0,0,0.06)",
                      padding: "2px 8px",
                    }}
                  >
                    {createdAdmin.generated_password ?? "—"}
                  </code>
                </div>
              </div>

              <div className="eyebrow" style={{ marginTop: 24, marginBottom: 8 }}>
                Próximos pasos sugeridos
              </div>
              <ul style={{ marginBottom: 24, color: "var(--ink-2)" }}>
                <li>
                  Configurar canal principal (WhatsApp/Email/Telegram) en{" "}
                  <Link to={`/tenants/${tenant.id}/canales`}>Canales</Link>.
                </li>
                <li>
                  Revisar y ajustar SLA en{" "}
                  <Link to={`/tenants/${tenant.id}/sla`}>SLA</Link>.
                </li>
                <li>
                  Cargar logo y colores en{" "}
                  <Link to={`/tenants/${tenant.id}/branding`}>Branding</Link>.
                </li>
              </ul>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => nav(`/tenants/${tenant.id}/funcionarios`)}
                >
                  Ir a funcionarios
                </button>
                <button
                  type="button"
                  className="cta"
                  style={{ width: "auto", flex: 1 }}
                  onClick={() => nav("/tenants")}
                >
                  <span className="cta-inner">Volver al listado</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Shell>
  );
}

function StepBar({ step }: { step: Step }) {
  const items: { n: Step; label: string }[] = [
    { n: 1, label: "Datos" },
    { n: 2, label: "Admin" },
    { n: 3, label: "Listo" },
  ];
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        marginBottom: 24,
        padding: 16,
        background: "var(--bone-warm)",
        borderRadius: 10,
        border: "1px solid var(--hairline)",
      }}
    >
      {items.map((it, i) => {
        const done = step > it.n;
        const active = step === it.n;
        return (
          <div
            key={it.n}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              opacity: done || active ? 1 : 0.45,
              flex: i < items.length - 1 ? 1 : "0 0 auto",
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: "50%",
                background: done
                  ? "var(--ok)"
                  : active
                    ? "var(--ink-1)"
                    : "transparent",
                color: "white",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                border: active || done ? "none" : "1px solid var(--ink-3)",
              }}
            >
              {done ? "✓" : it.n}
            </span>
            <span
              style={{
                fontWeight: active ? 700 : 500,
                color: active ? "var(--ink-1)" : "var(--ink-2)",
              }}
            >
              {it.label}
            </span>
            {i < items.length - 1 && (
              <span
                style={{
                  flex: 1,
                  height: 1,
                  background: "var(--hairline-strong)",
                  marginLeft: 8,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
